import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { UserInfoService } from '@fxp/fxpservices';

/**
 * Resolve the current manager alias from the FXP shell (OBO-aware: `getCurrentUser` returns the
 * acted-on manager, not the signed-in user). Returns '' when the FXP shell isn't present
 * (tests / local preview); callers surface that unavailable state explicitly. Never throws.
 */
export function getCurrentManagerAlias(): string {
  try {
    const userInfo = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.UserInfoService
    ) as UserInfoService;
    const alias = userInfo?.getCurrentUser?.();
    return alias ? String(alias).toLowerCase() : '';
  } catch {
    return '';
  }
}

/** The subset of the FXP profile we read; kept structural so tests need no FXP shell. */
interface UserProfileLike {
  preferredFirstName?: string;
  displayName?: string;
  DisplayName?: string;
  firstName?: string;
}

/**
 * Display name for the current (OBO-aware) manager, used by the "<name>'s Team" header.
 *
 * Parity with legacy `fmdashboard.controller.js:164-166`, which resolved
 * `preferredFirstName || DisplayName` and fell back to the raw alias when the profile call failed
 * (`:176`). Returns '' when the FXP shell isn't present so callers can use a neutral label.
 */
export function getCurrentManagerName(): string {
  const alias = getCurrentManagerAlias();
  try {
    const userInfo = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.UserInfoService
    ) as UserInfoService;
    const profile = userInfo?.getCurrentUserProfile?.() as UserProfileLike | undefined;
    const name =
      profile?.preferredFirstName ||
      profile?.displayName ||
      profile?.DisplayName ||
      profile?.firstName;
    return (name && String(name).trim()) || alias;
  } catch {
    return alias;
  }
}
